import React, { useState, useEffect, useRef } from 'react';
import { FiChevronDown, FiChevronRight, FiEdit2, FiArrowUp, FiArrowDown, FiTrash2, FiSquare } from 'react-icons/fi';
import { useEditing } from './EditingContext';
import EditStyleDialog from './components/EditStyleDialog';
import Task from './components/Task';

// Zentrale Opazität für alle Panels (0.0 = komplett transparent, 1.0 = deckend)
const PANEL_OPACITY = 0.4;

// Hilfsfunktion um eine beliebige Farbe (hex, rgb, rgba) mit definierter Opazität zurückzugeben
const applyOpacityToColor = (color, opacity) => {
  if (!color) return `rgba(255, 255, 255, ${opacity})`;

  // Wenn bereits rgba, nur die Alpha-Komponente ersetzen
  if (color.startsWith('rgba')) {
    return color.replace(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d.]+\)/, `rgba($1, $2, $3, ${opacity})`);
  }

  // Wenn rgb, zu rgba erweitern
  if (color.startsWith('rgb')) {
    return color.replace(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/, `rgba($1, $2, $3, ${opacity})`);
  }

  // Wenn Hex, zu rgba konvertieren
  if (color.startsWith('#')) {
    let hex = color.replace('#', '');
    if (hex.length === 3) {
      hex = hex.split('').map(c => c + c).join('');
    }
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  return `rgba(255, 255, 255, ${opacity})`;
};

export default function CollapsiblePanel({ 
  title, 
  children, 
  isAuthor, 
  canMoveUp, 
  canMoveDown, 
  onMoveUp, 
  onMoveDown, 
  textColor = '#000000', 
  backgroundColor = 'rgba(253, 255, 229, 0.9)',
  borderStyle = 'solid',
  borderColor = '#ddd',
  borderThickness = '1',
  onSave,
  onDelete,
  visibleWeeks = 10,
  weekWidth = 200,
  isVisibleWeeksChanging = false,
  isZoomChanging = false,
  movableBars = [],
  onBarsChange,
  onInfoPanelsChange,
  batchOperations,
  panelId,
  open: openProp = true,
  onOpenChange
}) {
  const [open, setOpen] = useState(openProp);
  const [editing, setEditing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editTitle, setEditTitle] = useState(title);
  const [editTextColor, setEditTextColor] = useState(textColor);
  const [editBackgroundColor, setEditBackgroundColor] = useState(backgroundColor);
  const [editBorderStyle, setEditBorderStyle] = useState(borderStyle);
  const [editBorderColor, setEditBorderColor] = useState(borderColor);
  const [editBorderThickness, setEditBorderThickness] = useState(borderThickness);
  const taskRef = useRef(null);
  const savedOpenStateRef = useRef(true); // Für die Wiederherstellung des Zustands

  // Globaler Editing-Status
  const { showEditButtons, setIsEditing } = useEditing();

  // Alle Task-IDs für dieses Panel generieren
  const getAllPanelTaskIds = () => {
    return (movableBars || []).map(bar => `${panelId}_${bar.id}`);
  };

  // Toggle für alle Panel-Tasks
  const handleToggleAllTasks = (e) => {
    e.stopPropagation();
    if (batchOperations && batchOperations.togglePanelTasks) {
      const allTaskIds = getAllPanelTaskIds();
      batchOperations.togglePanelTasks(allTaskIds);
    }
  };



  // Sync open state with prop
  useEffect(() => {
    setOpen(openProp);
  }, [openProp]);

  // Sync state with props when they change
  useEffect(() => {
    setEditTitle(title);
    setEditTextColor(textColor);
    setEditBackgroundColor(backgroundColor);
    setEditBorderStyle(borderStyle);
    setEditBorderColor(borderColor);
    setEditBorderThickness(borderThickness);
  }, [title, textColor, backgroundColor, borderStyle, borderColor, borderThickness]);

  // Handle repositioning when panel opens
  useEffect(() => {
    if (open && taskRef.current) {
      // Small delay to ensure DOM has been updated after opening
      const timeoutId = setTimeout(() => {
        if (taskRef.current && taskRef.current.forceRepositioning) {
          taskRef.current.forceRepositioning();
        }
      }, 200);
      
      return () => clearTimeout(timeoutId);
    }
  }, [open]);

  // Globale Event-Handler für CollapsiblePanels
  useEffect(() => {
    const handleSaveAndClose = () => {
      // Speichere aktuellen Zustand für spätere Wiederherstellung
      savedOpenStateRef.current = open;
      // Schließe Panel
      setOpen(false);
    };

    const handleRestore = () => {
      // Stelle gespeicherten Zustand wieder her
      setOpen(savedOpenStateRef.current);
    };

    // Event-Listener hinzufügen
    window.addEventListener('saveAndCloseAllCollapsiblePanels', handleSaveAndClose);
    window.addEventListener('restoreCollapsiblePanels', handleRestore);

    // Cleanup beim Unmount
    return () => {
      window.removeEventListener('saveAndCloseAllCollapsiblePanels', handleSaveAndClose);
      window.removeEventListener('restoreCollapsiblePanels', handleRestore);
    };
  }, [open]); // open als Dependency, damit der aktuelle Zustand gespeichert wird

  const handleEdit = () => {
    setEditing(true);
    setIsEditing(true);
  };
  const handleCancel = () => {
    setEditTitle(title);
    setEditTextColor(textColor);
    setEditBackgroundColor(backgroundColor);
    setEditBorderStyle(borderStyle);
    setEditBorderColor(borderColor);
    setEditBorderThickness(borderThickness);
    setEditing(false);
    setIsEditing(false);
  };
  const handleSave = () => {
    setEditing(false);
    setIsEditing(false);
    
    // Get current bars from Task component
    const currentBars = taskRef.current ? taskRef.current.getBars() : [];
    
    // Callback für Titel- und Farbänderung aufrufen
    if (onSave) {
      // Erzwinge die zentrale Opazität auf die Hintergrundfarbe
      const backgroundColorWithOpacity = applyOpacityToColor(editBackgroundColor, PANEL_OPACITY);
      onSave(editTitle, editTextColor, backgroundColorWithOpacity, editBorderStyle, editBorderColor, editBorderThickness, currentBars);
    }
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    setShowDeleteDialog(false);
    setEditing(false);
    setIsEditing(false);
    if (onDelete) {
      onDelete();
    }
  };

  // Force repositioning when panel opens/closes
  const handleToggleOpen = () => {
    const newOpenState = !open;
    setOpen(newOpenState);

    // Zustand nach oben propagieren (für Minimap und getData())
    if (onOpenChange) {
      onOpenChange(newOpenState);
    }

    // Force repositioning and visibility update for both opening and closing
    if (taskRef.current) {
      // Close any open info panels first
      if (taskRef.current.closeAllInfoPanels) {
        taskRef.current.closeAllInfoPanels();
      }
      
      // Force repositioning after DOM update
      setTimeout(() => {
        if (taskRef.current && taskRef.current.forceRepositioning) {
          taskRef.current.forceRepositioning();
        }
        
        // Ensure bars are visible when panel opens
        if (newOpenState && taskRef.current.ensureBarsVisible) {
          taskRef.current.ensureBarsVisible();
        }
        
        // Also trigger a resize event as fallback
        window.dispatchEvent(new Event('resize'));
      }, 100); // Slightly shorter delay for better responsiveness
    }
  };

  return (
    <div
      data-panel-id={panelId}
      data-panel-open={open}
      style={{
      borderTop: `${editBorderThickness}px ${editBorderStyle} ${editBorderColor}`,
      borderBottom: `${editBorderThickness}px ${editBorderStyle} ${editBorderColor}`,
      borderLeft: 'none',
      borderRight: 'none',
      borderRadius: 6,
      marginBottom: 16,
      background: applyOpacityToColor(editBackgroundColor, PANEL_OPACITY),
      boxShadow: '0 1px 4px rgba(0,0,0,0.04)'
    }}>
      <div
        style={{
          display: 'flex',
          alignItems: editing ? 'flex-start' : 'center',
          cursor: editing ? 'default' : 'pointer',
          padding: '12px 0px',
          fontWeight: 'bold',
          fontSize: 18,
          userSelect: 'none',
          color: editTextColor,
          minHeight: editing ? 'auto' : '28px'
        }}
        onClick={editing ? undefined : handleToggleOpen}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            position: 'sticky',
            left: 0,
            backgroundColor: applyOpacityToColor(editBackgroundColor, PANEL_OPACITY),
            paddingRight: '8px',
            zIndex: 10
          }}>
          {!editing && (open ? <FiChevronDown /> : <FiChevronRight />)}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: editing ? 'flex-start' : 'center',
            marginLeft: editing ? 0 : 0,
            flex: 1,
            justifyContent: 'space-between',
            flexDirection: editing ? 'column' : 'row'
          }}
          onClick={e => e.stopPropagation()}
        >
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            flex: 1, 
            width: '100%'
          }}>
            {editing ? (
              <EditStyleDialog
                editTitle={editTitle}
                setEditTitle={setEditTitle}
                editTextColor={editTextColor}
                setEditTextColor={setEditTextColor}
                editBackgroundColor={editBackgroundColor}
                setEditBackgroundColor={setEditBackgroundColor}
                editBorderStyle={editBorderStyle}
                setEditBorderStyle={setEditBorderStyle}
                editBorderColor={editBorderColor}
                setEditBorderColor={setEditBorderColor}
                editBorderThickness={editBorderThickness}
                setEditBorderThickness={setEditBorderThickness}
                onSave={handleSave}
                onCancel={handleCancel}
                onDelete={onDelete ? handleDelete : undefined}
                titlePlaceholder="Panel-Titel eingeben"
                textColorPlaceholder="#000000"
                backgroundColorPlaceholder="#fafafa"
              />
            ) : (
              <span style={{ 
                marginLeft: 0,
                position: 'sticky',
                left: '32px',
                backgroundColor: applyOpacityToColor(editBackgroundColor, PANEL_OPACITY),
                paddingRight: '8px',
                zIndex: 9
              }}>{editTitle}</span>
            )}
          </div>
          {!editing && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px',
              position: 'sticky',
              right: 0,
              backgroundColor: applyOpacityToColor(editBackgroundColor, PANEL_OPACITY),
              paddingLeft: '8px',
              zIndex: 10
            }}>
              {/* Toggle-Icon für Batch-Auswahl aller Panel-Tasks */}
              {batchOperations && batchOperations.isSelectionMode && movableBars && movableBars.length > 0 && (
                <span
                  style={{ 
                    cursor: 'pointer', 
                    padding: '4px',
                    borderRadius: '3px',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#666',
                    transition: 'background-color 0.2s',
                    marginRight: '4px'
                  }}
                  onClick={handleToggleAllTasks}
                  onMouseEnter={e => e.target.style.backgroundColor = '#e0e0e0'}
                  onMouseLeave={e => e.target.style.backgroundColor = 'transparent'}
                  title="Tasks invertieren (ausgewählte abwählen, nicht ausgewählte auswählen)"
                >
                  <FiSquare size={16} />
                </span>
              )}
              {isAuthor && showEditButtons && (canMoveUp || canMoveDown) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '2px', marginRight: '4px' }}>
                  {canMoveUp && (
                    <span
                      style={{ 
                        cursor: 'pointer', 
                        padding: '4px',
                        borderRadius: '3px',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#666',
                        transition: 'background-color 0.2s'
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        onMoveUp && onMoveUp();
                      }}
                      onMouseEnter={e => e.target.style.backgroundColor = '#e0e0e0'}
                      onMouseLeave={e => e.target.style.backgroundColor = 'transparent'}
                      title="Nach oben verschieben"
                    >
                      <FiArrowUp size={16} />
                    </span>
                  )}
                  {canMoveDown && (
                    <span
                      style={{ 
                        cursor: 'pointer', 
                        padding: '4px',
                        borderRadius: '3px',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#666',
                        transition: 'background-color 0.2s'
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        onMoveDown && onMoveDown();
                      }}
                      onMouseEnter={e => e.target.style.backgroundColor = '#e0e0e0'}
                      onMouseLeave={e => e.target.style.backgroundColor = 'transparent'}
                      title="Nach unten verschieben"
                    >
                      <FiArrowDown size={16} />
                    </span>
                  )}
                </div>
              )}
              {isAuthor && showEditButtons && (
                <span
                  style={{ cursor: 'pointer', padding: '4px' }}
                  onClick={e => {
                    e.stopPropagation();
                    handleEdit();
                  }}
                  title="Titel, Farben und Border bearbeiten"
                >
                  <FiEdit2 size={16} />
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      {open && (
        <div style={{ padding: '4px 0px', borderTop: '1px solid #eee' }}>
          <Task 
            ref={taskRef}
            visibleWeeks={visibleWeeks} 
            weekWidth={weekWidth}
            isVisibleWeeksChanging={isVisibleWeeksChanging}
            isZoomChanging={isZoomChanging}
            initialBars={movableBars || []}
            onBarsChange={onBarsChange}
            onInfoPanelsChange={onInfoPanelsChange}
            batchOperations={batchOperations}
            panelId={panelId}
          />
        </div>
      )}

      {/* Löschen-Bestätigungsdialog */}
      {showDeleteDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            minWidth: '400px',
            maxWidth: '90vw'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#ff5722' }}>
              <FiTrash2 style={{ marginRight: '8px' }} />
              Panel löschen
            </h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '14px', lineHeight: '1.5' }}>
              Sind Sie sicher, dass Sie das Panel <strong>"{editTitle}"</strong> löschen möchten?
            </p>
            <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#666', fontStyle: 'italic' }}>
              Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
                onClick={() => setShowDeleteDialog(false)}
              >
                Abbrechen
              </button>
              <button
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#ff5722',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onClick={confirmDelete}
              >
                Panel löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}